/**
 * Visible text of a rendered reference, and the comparison keys used to find cited passages in it.
 * Rendered HTML is read here; other viewers build the same shape from their own text.
 *
 * Passages are compared on letters, numbers and combining marks only, lowercased. Everything else
 * (spaces, punctuation, Markdown syntax, invisible characters, hyphen and dash variants) is ignored,
 * so text that looks the same on the page compares equal even when formatting tags, missing spaces
 * or special hyphens split it differently.
 */

import type { ListItem } from 'mdast'
import remarkGfm from 'remark-gfm'
import remarkParse from 'remark-parse'
import { unified } from 'unified'
import { visit } from 'unist-util-visit'

const kKeyChar = /[\p{L}\p{N}\p{M}]/u
const kInvisibleChar = /[\u00AD\u200B-\u200D\u2060\uFEFF]/u
const kHyphenChar = /[\u2010-\u2015\u2212]/u
const kHtmlTag = /<\/?[a-zA-Z][^>]*>/g
const kMarkdownParser = unified().use(remarkParse).use(remarkGfm)
const kListItemMarker = /^(?:\d{1,9}[.)]|[-*+])/
const kMarkdownHeading = /^\s*#{1,6}\s/
const kBlockTags = new Set([
  'ADDRESS',
  'ARTICLE',
  'ASIDE',
  'BLOCKQUOTE',
  'BR',
  'CAPTION',
  'DD',
  'DIV',
  'DL',
  'DT',
  'FIGCAPTION',
  'FIGURE',
  'FOOTER',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'HEADER',
  'HR',
  'LI',
  'MAIN',
  'NAV',
  'OL',
  'P',
  'PRE',
  'SECTION',
  'TABLE',
  'TD',
  'TH',
  'TR',
  'UL',
])
const kHeadingTags = new Set(['H1', 'H2', 'H3', 'H4', 'H5', 'H6'])

export enum BlockKind {
  HEADING = 'heading',
  BODY = 'body',
}

/** A paragraph, heading, list item or cell of the rendered document. */
export type TextBlock = {
  kind: BlockKind
  text: string
  /** Known from the document itself, such as a Word table-of-contents style. */
  isTableOfContents: boolean
}

/**
 * One visible character, or a block boundary (`location` null) that keeps blocks from running
 * together. `location` is where the viewer can paint the character.
 */
export type VisibleChar<Location> = {
  location: Location | null
  char: string
  isKey: boolean
  block: TextBlock | null
}

export type VisibleText<Location> = {
  chars: VisibleChar<Location>[]
  blocks: TextBlock[]
  /** Lowercased key characters joined; `keyToChar[i]` is the index in `chars` of key character i. */
  key: string
  /** The same key characters in their original case. */
  casedKey: string
  keyToChar: number[]
}

/** A character of a rendered HTML page: its text node and UTF-16 offset in it. */
export type DomLocation = { node: Text; offset: number }

export function normalizeChar(char: string): string {
  if (kHyphenChar.test(char)) return '-'
  if (char === '\u201C' || char === '\u201D') return '"'
  if (char === '\u2018' || char === '\u2019') return "'"
  return char
}

export function isKeyChar(char: string): boolean {
  return kKeyChar.test(char)
}

/**
 * Lowercase one character on its own, so page text and passages fold the same way whatever
 * surrounds them. Final sigma compares as ordinary sigma, since capital sigma lowercases to the
 * ordinary form. When lowercasing lengthens a character, the cased form uses the lowercase units so
 * both keys stay the same length.
 */
function keyOf(char: string): { key: string; casedKey: string } {
  const key = char === '\u03C2' ? '\u03C3' : char.toLowerCase()
  return { key, casedKey: key.length === char.length ? char : key }
}

function isBlockElement(element: Element): boolean {
  return kBlockTags.has(element.tagName)
}

/** Append a boundary that keeps two blocks from running together, unless one ends the text already. */
export function pushBlockBoundary<Location>(chars: VisibleChar<Location>[]): void {
  if (chars.length > 0 && chars[chars.length - 1].location !== null) chars.push({ location: null, char: '\n', isKey: false, block: null })
}

/** Append one character of page text, skipping invisible characters and folding hyphens and quotes. */
export function pushVisibleChar<Location>(chars: VisibleChar<Location>[], char: string, location: Location, block: TextBlock | null): void {
  if (!kInvisibleChar.test(char)) chars.push({ location, char: normalizeChar(char), isKey: isKeyChar(char), block })
}

/** Read every visible character under `root` in document order, with a boundary at each block edge. */
export function readVisibleText(root: Node): VisibleText<DomLocation> {
  const chars: VisibleChar<DomLocation>[] = []
  const blocks: TextBlock[] = []
  const walk = (node: Node, block: TextBlock | null) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node as Text
      let offset = 0
      for (const char of text.data) {
        pushVisibleChar(chars, char, { node: text, offset }, block)
        offset += char.length
      }
      return
    }
    if (!(node instanceof Element)) {
      node.childNodes.forEach((child) => walk(child, block))
      return
    }
    if (!isBlockElement(node)) {
      node.childNodes.forEach((child) => walk(child, block))
      return
    }
    const ownBlock = { kind: kHeadingTags.has(node.tagName) ? BlockKind.HEADING : BlockKind.BODY, text: node.textContent ?? '', isTableOfContents: false }
    blocks.push(ownBlock)
    pushBlockBoundary(chars)
    node.childNodes.forEach((child) => walk(child, ownBlock))
    pushBlockBoundary(chars)
  }
  walk(root, null)
  return buildVisibleText(chars, blocks)
}

/** Fold the key characters of `chars` into the comparison keys. */
export function buildVisibleText<Location>(chars: VisibleChar<Location>[], blocks: TextBlock[]): VisibleText<Location> {
  let key = ''
  let casedKey = ''
  const keyToChar: number[] = []
  chars.forEach((entry, index) => {
    if (!entry.isKey) return
    const folded = keyOf(entry.char)
    key += folded.key
    casedKey += folded.casedKey
    // Every unit of a lengthened character maps back to the same character.
    for (let unit = 0; unit < folded.key.length; unit++) keyToChar.push(index)
  })
  return { chars, blocks, key, casedKey, keyToChar }
}

/** Compared characters, lowercased and in their original case. */
export type KeyText = { key: string; casedKey: string }

export type PassageText = {
  /** Visible passage text: tags removed, entities decoded. */
  text: string
  key: string
  casedKey: string
  words: KeyText[]
  blockKind: BlockKind
}

/**
 * Drop the Markdown list markers the renderer turns into list numbering, bullets or task
 * checkboxes, which the page draws outside its text. The passage is parsed with the renderer's own
 * Markdown parser, so a number stays wherever the page shows it as text: inside a sentence, after a
 * line break in a paragraph, in a code block, or as a lettered marker such as "a.".
 */
function stripListMarkers(passage: string): string {
  const markers: [number, number][] = []
  visit(kMarkdownParser.parse(passage), 'listItem', (item: ListItem) => {
    const start = item.position?.start.offset
    if (start === undefined) return
    // Everything before the item's first text is marker, spacing or checkbox, on one line or more.
    // A task item's paragraph starts at its checkbox, so the first text inside the paragraph is used.
    const first = item.children[0]
    const contentStart = first?.type === 'paragraph' ? first.children[0]?.position?.start.offset ?? first.position?.start.offset : first?.position?.start.offset
    const end = contentStart ?? start + (kListItemMarker.exec(passage.slice(start))?.[0].length ?? 0)
    if (end > start) markers.push([start, end])
  })
  return markers.reduceRight((text, [start, end]) => text.slice(0, start) + text.slice(end), passage)
}

/** Read a stored passage the way it appears on the page: no HTML tags or list markers, entities decoded. */
export function readPassage(passage: string): PassageText {
  const text = decodeHtmlEntities(stripListMarkers(passage).replace(kHtmlTag, ''))
  const words = text
    .split(/\s+/u)
    .map((word) => {
      const folded = [...word].filter((char) => !kInvisibleChar.test(char) && isKeyChar(char)).map(keyOf)
      return { key: folded.map((char) => char.key).join(''), casedKey: folded.map((char) => char.casedKey).join('') }
    })
    .filter((word) => word.key.length > 0)
  return {
    text,
    key: words.map((word) => word.key).join(''),
    casedKey: words.map((word) => word.casedKey).join(''),
    words,
    blockKind: kMarkdownHeading.test(text) ? BlockKind.HEADING : BlockKind.BODY,
  }
}

const kChunkWords = 10

/** Split a passage into runs of about ten words, folding a short tail into the last run. */
export function chunkPassage(passage: PassageText): KeyText[] {
  const chunks: KeyText[] = []
  for (let start = 0; start < passage.words.length; start += kChunkWords) {
    const words = passage.words.slice(start, start + kChunkWords)
    const chunk = { key: words.map((word) => word.key).join(''), casedKey: words.map((word) => word.casedKey).join('') }
    const last = chunks[chunks.length - 1]
    if (last && words.length < kChunkWords / 2) chunks[chunks.length - 1] = { key: last.key + chunk.key, casedKey: last.casedKey + chunk.casedKey }
    else chunks.push(chunk)
  }
  return chunks
}

/** Every start index of `needle` in `haystack`, overlapping occurrences included. */
export function findAll(haystack: string, needle: string): number[] {
  const starts: number[] = []
  if (!needle) return starts
  for (let start = haystack.indexOf(needle); start !== -1; start = haystack.indexOf(needle, start + 1)) starts.push(start)
  return starts
}

function decodeHtmlEntities(text: string): string {
  const textarea = document.createElement('textarea')
  textarea.innerHTML = text
  return textarea.value
}
